import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  Form,
  Row,
  Col,
  Input,
  Tag,
  Button,
  Typography,
  message,
  Tooltip,
  Modal,
} from 'antd';
import {
  PaperClipOutlined,
  PictureOutlined,
  CodeOutlined,
  LoadingOutlined,
  BoldOutlined,
  ItalicOutlined,
  StrikethroughOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { CopyBlock } from 'react-code-blocks';
import { useTaskContext } from '../../../../core/components/tasks/useTaskContext';
import { S3Image } from '../../CampaignLauncher/components';
import S3File from '../../../shared/S3File';
import AvatarS3 from '../../../shared/images/AvatarS3';
import { startCase } from 'lodash';
import { codeBlock, handleUploadFilesToS3 } from '../utils/task.utils';
import { getRelativeTime } from '../../../shared/GetRelativeTime';
import FileDropPasteArea from '../utils/FileDropPasteArea';
import TaskEditableCommentDetails from './TaskEditableCommentDetails';
import '../styles/SelectInput.css';
import '../styles/CommentIcons.css';
import { allowedFileExtensions, allowedImageExtensions } from '../configs/task.config';

const { Text } = Typography;

export const TaskComments = (props) => {
  const [form] = Form.useForm();
  const textareaRef = useRef(null);
  const { currentUser, createComment, refetchTask, updateComment, updateCommentLoading } =
    useTaskContext();

  useEffect(() => {
    if (props.formRef) {
      props.formRef.current = form;
    }
  }, [form, props.formRef]);

  const currentTask = props;
  const currentTaskId = currentTask && currentTask.currentTask && currentTask.currentTask.id;
  const createdBy = startCase(
    currentTask &&
      currentTask.currentTask &&
      currentTask.currentTask.createdBy &&
      currentTask.currentTask.createdBy.name
      ? currentTask.currentTask.createdBy.name
      : currentTask &&
          currentTask.currentTask &&
          currentTask.currentTask.createdBy &&
          currentTask.currentTask.createdBy.username
  );
  const level =
    currentUser && currentUser.role && currentUser.role.org && currentUser.role.org.level;
  const initialAvatar =
    currentTask &&
    currentTask.currentTask &&
    currentTask.currentTask.createdBy &&
    currentTask.currentTask.createdBy.avatar &&
    currentTask.currentTask.createdBy.avatar.key
      ? currentTask.currentTask.createdBy.avatar.key
      : null;

  const initialComments = {
    id: 1,
    createdBy: createdBy,
    avatar: initialAvatar,
    createdAt:
      currentTask && currentTask.currentTask && currentTask.currentTask.createdAt
        ? currentTask.currentTask.createdAt || new Date().toISOString()
        : '-',
    comment:
      currentTask && currentTask.currentTask && currentTask.currentTask.taskDetails
        ? currentTask.currentTask.taskDetails
        : 'No task details available',
    attachedFiles:
      currentTask && currentTask.currentTask && currentTask.currentTask.attachedFiles
        ? currentTask.currentTask.attachedFiles
        : [],
    attachedImages:
      currentTask && currentTask.currentTask && currentTask.currentTask.attachedImages
        ? currentTask.currentTask.attachedImages
        : [],
    codeSnippet:
      currentTask && currentTask.currentTask && currentTask.currentTask.codeSnippet
        ? currentTask.currentTask.codeSnippet
        : [],
  };

  // UseState for some handling functionality
  const [buttonDisabled, setButtonDisabled] = useState(false); // Used state for fast disable
  const [editingCommentId, setEditingCommentId] = useState(null); // Used state for editing comment
  const [deleteModal, setDeleteModal] = useState({
    visible: false,
    commentId: null,
  }); // Used state for delete modal visibility and comment id

  const comments = [initialComments];
  const bottomRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null);

  const handleUploadFileClick = () => {
    fileInputRef.current.click();
  };

  const handleUploadImageClick = () => {
    imageInputRef.current.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const invalidFiles = files.filter(
      (file) =>
        !allowedFileExtensions.some((ext) =>
          file.name.toLowerCase().endsWith(ext.replace('.', '').toLowerCase())
        )
    );
    if (invalidFiles.length > 0) {
      message.error('Images are not allowed in this field.');
      e.target.value = null;
      return;
    }
    form.setFieldsValue({
      attachedFiles: [...(form.getFieldValue('attachedFiles') || []), ...files],
    });
    e.target.value = null;
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const invalidFiles = files.filter(
      (file) =>
        !allowedImageExtensions.some((ext) =>
          file.name.toLowerCase().endsWith(ext.replace('.', '').toLowerCase())
        )
    );
    if (invalidFiles.length > 0) {
      message.error('Only image files are allowed.');
      e.target.value = null;
      return;
    }
    form.setFieldsValue({
      attachedImages: [...(form.getFieldValue('attachedImages') || []), ...files],
    });
    e.target.value = null;
  };

  const handleRemoveFile = (index) => {
    const attachedFiles = form.getFieldValue('attachedFiles') || [];
    attachedFiles.splice(index, 1);
    form.setFieldsValue({ attachedFiles });
  };

  const handleRemoveImage = (index) => {
    const attachedImages = form.getFieldValue('attachedImages') || [];
    attachedImages.splice(index, 1);
    form.setFieldsValue({ attachedImages });
  };

  const handleInsertCode = () => {
    const textarea =
      textareaRef.current &&
      textareaRef.current.resizableTextArea &&
      textareaRef.current.resizableTextArea.textArea;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const currentValue = form.getFieldValue('comment') || '';
    const selectedText = currentValue.slice(start, end);

    let newValue, cursorPosition;

    if (selectedText) {
      // Wrap selected text with code blocks
      newValue =
        currentValue.slice(0, start) + '```\n' + selectedText + '\n```' + currentValue.slice(end);
      cursorPosition = start + selectedText.length + 8;
    } else {
      // Insert empty code block
      newValue = currentValue.slice(0, start) + '```\n\n```' + currentValue.slice(end);
      cursorPosition = start + 4;
    }

    // Update form value
    form.setFieldsValue({ comment: newValue });

    // Set cursor position after update
    setTimeout(() => {
      textarea.selectionStart = cursorPosition;
      textarea.selectionEnd = cursorPosition;
      textarea.focus();
    }, 0);
  };

  const parseMarkdown = (text) => {
    const elements = [];
    const lines = text.split('\n');
    let currentList = [];

    const processInline = (content) => {
      const parts = [];
      const regex = /(\*\*)(.*?)\1|(\*)(.*?)\3|(~~)(.*?)\5|(`)(.*?)\7|(https?:\/\/\S+)|(.)/gs;
      let lastIndex = 0;
      let match;

      while ((match = regex.exec(content)) !== null) {
        const [
          full, // eslint-disable-line no-unused-vars
          boldOpen,
          boldContent,
          italicOpen,
          italicContent,
          strikeOpen,
          strikeContent,
          codeOpen,
          codeContent,
          url,
          other,
        ] = match;

        if (match.index > lastIndex) {
          parts.push(content.slice(lastIndex, match.index));
        }

        if (boldOpen) {
          parts.push(<strong key={`bold-${match.index}`}>{boldContent}</strong>);
        } else if (italicOpen) {
          parts.push(<em key={`italic-${match.index}`}>{italicContent}</em>);
        } else if (strikeOpen) {
          parts.push(<del key={`strike-${match.index}`}>{strikeContent}</del>);
        } else if (codeOpen) {
          parts.push(<code key={`code-${match.index}`}>{codeContent}</code>);
        } else if (url) {
          parts.push(
            <a key={`link-${match.index}`} href={url} target="_blank" rel="noopener noreferrer">
              {url}
            </a>
          );
        } else {
          parts.push(other);
        }

        lastIndex = regex.lastIndex;
      }

      if (lastIndex < content.length) {
        parts.push(content.slice(lastIndex));
      }

      return parts;
    };

    // Process each line
    lines.forEach((line, index) => {
      if (line.startsWith('- ')) {
        const content = line.substring(2);
        currentList.push(<li key={`li-${index}`}>{processInline(content)}</li>);
      } else {
        if (currentList.length > 0) {
          elements.push(<ul key={`ul-${index}`}>{currentList}</ul>);
          currentList = [];
        }
        if (line.trim()) {
          elements.push(<span key={`line-${index}`}>{processInline(line)}</span>);
        }
        if (index !== lines.length - 1) {
          elements.push(<br key={`br-${index}`} />);
        }
      }
    });

    // Add remaining list items
    if (currentList.length > 0) {
      elements.push(<ul key="ul-end">{currentList}</ul>);
    }

    return elements;
  };

  const insertText = (options) => {
    const textarea =
      textareaRef.current &&
      textareaRef.current.resizableTextArea &&
      textareaRef.current.resizableTextArea.textArea;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const value = textarea.value;
    const selectedText = value.substring(start, end);

    let transformedText, newStart, newEnd;

    if (options.transform) {
      const result = options.transform(selectedText, start, end);
      transformedText = result.text;
      newStart = result.newStart;
      newEnd = result.newEnd;
    } else {
      const { before, after } = options;
      transformedText = before + selectedText + after;
      newStart = start + before.length;
      newEnd = newStart + selectedText.length;
    }

    const newValue = value.substring(0, start) + transformedText + value.substring(end);

    form.setFieldsValue({ comment: newValue });

    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newStart, newEnd);
    }, 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      const textarea = textareaRef.current.resizableTextArea.textArea;
      const value = form.getFieldValue('comment') || '';
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      const lineStart = value.lastIndexOf('\n', start - 1) + 1;
      const lineEnd = value.indexOf('\n', start);
      const currentLine = value.slice(lineStart, lineEnd === -1 ? value.length : lineEnd);

      const unorderedMatch = currentLine.match(/^-\s/);
      const orderedMatch = currentLine.match(/^(\d+)\.\s/);

      if (unorderedMatch || orderedMatch) {
        e.preventDefault();
        let newValue, newCursorPos;

        if (unorderedMatch) {
          // Handle unordered list
          newValue = `${value.slice(0, start)}\n- ${value.slice(end)}`;
          newCursorPos = start + 3; // Position after "- "
        } else {
          // Handle ordered list
          const nextNumber = parseInt(orderedMatch[1]) + 1;
          newValue = `${value.slice(0, start)}\n${nextNumber}. ${value.slice(end)}`;
          newCursorPos = start + `${nextNumber}. `.length + 1;
        }

        form.setFieldsValue({ comment: newValue });
        setTimeout(() => {
          textarea.selectionStart = newCursorPos;
          textarea.selectionEnd = newCursorPos;
        }, 0);
      }
    }
  };

  const handleSubmitComment = async (values) => {
    const { comment, attachedFiles, attachedImages, attachedCodeSnippet } = values;
    let mediaImages = [];
    let mediaFiles = [];

    if (
      (!comment || !comment.trim()) &&
      (!attachedFiles || !attachedFiles.length) &&
      (!attachedImages || !attachedImages.length) &&
      (!attachedCodeSnippet || !attachedCodeSnippet.trim())
    ) {
      message.destroy();
      message.error('Comment or an attachment is required.');
      return;
    }

    try {
      setButtonDisabled(true);
      message.loading('Posting comment...', 0);

      if (attachedImages && attachedImages.length) {
        mediaImages = await handleUploadFilesToS3(attachedImages);
      }

      if (attachedFiles && attachedFiles.length) {
        mediaFiles = await handleUploadFilesToS3(attachedFiles);
      }

      const commentData = {
        comment: comment || '',
        attachedImages: mediaImages.length ? { create: mediaImages } : {},
        attachedFiles: mediaFiles.length ? { create: mediaFiles } : {},
        codeSnippet: attachedCodeSnippet || '',
        createdBy: { connect: { id: currentUser.id } },
        task: { connect: { id: currentTaskId } },
      };

      await createComment({
        variables: {
          data: commentData,
        },
      });

      form.resetFields(); // Clear the form
      await refetchTask();

      // Notify parent to refresh task list
      if (props.onTaskUpdate) {
        props.onTaskUpdate();
      }

      message.destroy(); // Remove loading message
      message.success('Comment posted successfully!');
      setButtonDisabled(false);
    } catch (error) {
      message.destroy(); // Remove loading message
      message.error(`Failed to post comment: ${error.message}`);
      setButtonDisabled(false);
    }
  };

  const onButtonClick = () => {
    form.submit();
  };

  const displayedComments = [
    ...(comments || []), // initial comment
    ...((currentTask && currentTask.currentTask && currentTask.currentTask.attachedComments) || []),
  ];

  return (
    <div>
      <Form form={form} onFinish={handleSubmitComment} layout="vertical">
        <Card
          style={{
            border: '1px solid #e5edf4',
            borderRadius: '8px',
            position: 'relative',
            minHeight: '25vh',
            maxHeight: '100vh',
            display: 'flex',
            flexDirection: 'column',
          }}
          bodyStyle={{
            flex: 1,
            overflow: 'auto',
            minHeight: '0',
          }}
        >
          <Text strong style={{ fontSize: '18px' }}>
            ACTIVITY
          </Text>
          <Col
            style={{
              overflowY: 'auto',
              paddingRight: '4px',
              position: 'relative',
              height: '100%',
              minHeight: '0',
            }}
          >
            {displayedComments.map((comment, index) => (
              <Card
                key={comment.id}
                style={{ padding: 0, margin: 0, border: 'none' }}
                ref={index === displayedComments.length - 1 ? bottomRef : null}
                className="comment-row"
              >
                <Row
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                  }}
                >
                  <AvatarS3
                    imgKey={
                      comment.avatar ||
                      (comment.createdBy &&
                        comment.createdBy.avatar &&
                        comment.createdBy.avatar.key)
                    }
                    username={(comment.createdBy && comment.createdBy.name) || createdBy}
                    avatarProps={{ size: 45 }}
                    style={{ marginRight: 12 }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline' }}>
                      <Text
                        style={{
                          fontWeight: 'bold',
                          fontSize: 15,
                          paddingLeft: 19.5,
                        }}
                      >
                        {startCase(
                          comment.createdBy && typeof comment.createdBy === 'object'
                            ? comment.createdBy.name || comment.createdBy.username
                            : comment.createdBy || 'Unknown'
                        )}
                      </Text>
                      {!editingCommentId &&
                        level === 'PLATFORM' &&
                        ((comment.createdBy &&
                          typeof comment.createdBy === 'object' &&
                          comment.createdBy.id === currentUser.id) ||
                          comment.createdBy === currentUser.id) && (
                          <Row
                            className="comment-actions"
                            style={{
                              display: 'flex',
                              justifyContent: 'flex-end',
                              gap: '10px',
                              paddingLeft: 15,
                            }}
                          >
                            <EditOutlined
                              className="icon-hover-base icon-hover-edit"
                              disabled={updateCommentLoading}
                              onClick={() => {
                                setEditingCommentId(comment.id);
                              }}
                            />
                            <DeleteOutlined
                              className="icon-hover-base icon-hover-delete"
                              onClick={() => {
                                setDeleteModal({
                                  visible: true,
                                  commentId: comment.id,
                                });
                              }}
                            />
                          </Row>
                        )}
                      <span
                        style={{
                          color: 'gray',
                          fontSize: 14,
                          marginLeft: 'auto',
                        }}
                      >
                        {getRelativeTime(comment.createdAt)}
                      </span>
                    </div>
                    {editingCommentId === comment.id && level === 'PLATFORM' ? (
                      <TaskEditableCommentDetails
                        comment={comment}
                        isEditing={editingCommentId === comment.id}
                        setIsEditing={() => setEditingCommentId(null)}
                        refetchTask={refetchTask}
                        updateComment={updateComment}
                        onTaskUpdate={props.onTaskUpdate}
                      />
                    ) : (
                      <Text
                        style={{
                          display: 'block',
                          marginTop: 2,
                          fontSize: 14,
                          lineHeight: 1.4,
                          paddingLeft: 20,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          maxWidth: '100%',
                        }}
                      >
                        {(() => {
                          const rawText =
                            typeof comment.comment === 'object' && comment.comment !== null
                              ? comment.comment.text || 'No text available'
                              : comment.comment || '';

                          const processedParts = codeBlock(rawText);

                          return processedParts.map((part, index) => {
                            if (part.type === 'text') {
                              return (
                                <span key={`text-${index}`}>{parseMarkdown(part.content)}</span>
                              );
                            }

                            // Basic syntax highlighting using CSS
                            return (
                              <pre
                                key={`code-${index}`}
                                style={{
                                  backgroundColor: '#f0f0f0',
                                  borderRadius: '4px',
                                  overflowX: 'auto',
                                  margin: '0',
                                  fontFamily: 'monospace',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                }}
                              >
                                <code
                                  style={{
                                    color: '#333',
                                    display: 'block',
                                    fontSize: '13px',
                                  }}
                                >
                                  {part.content}
                                </code>
                              </pre>
                            );
                          });
                        })()}
                      </Text>
                    )}
                  </div>
                </Row>
                {comment.attachedFiles && !editingCommentId && (
                  <Row
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      paddingTop: '2px',
                      gap: '3px',
                      marginLeft: 65,
                      maxWidth: '100%',
                    }}
                  >
                    {comment.attachedFiles.map((file, index) => (
                      <S3File key={index} file={file} />
                    ))}
                  </Row>
                )}
                {comment.attachedImages && !editingCommentId && (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      gap: '3px',
                      paddingTop: '2px',
                      marginLeft: 65,
                      maxWidth: '100%',
                    }}
                  >
                    {comment.attachedImages.map((file, index) => (
                      <S3Image
                        key={index}
                        imgKey={file.key}
                        height="25%"
                        width="25%"
                        style={{ maxWidth: '100px' }}
                      />
                    ))}
                  </div>
                )}
                {typeof comment.codeSnippet === 'string' && comment.codeSnippet.trim() && (
                  <Col
                    style={{
                      maxWidth: '100%',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      overflowX: 'auto',
                    }}
                  >
                    <CopyBlock
                      text={comment.codeSnippet}
                      language="javascript"
                      wrapLongLines
                      showLineNumbers={false}
                      theme="dracula"
                      codeBlock
                    />
                  </Col>
                )}
              </Card>
            ))}
          </Col>
          <div
            style={{
              position: 'sticky',
              bottom: -25,
              backgroundColor: '#fff',
              zIndex: 1,
              padding: '8px 0',
            }}
          >
            <Row
              style={{
                display: 'flex',
                flexDirection: 'column',
                border: '1px solid #d9d9d9',
                borderRadius: '4px',
                position: 'relative',
              }}
            >
              <FileDropPasteArea
                onImagesAdded={(newImages) => {
                  const currentImages = form.getFieldValue('attachedImages') || [];
                  form.setFieldsValue({
                    attachedImages: [...currentImages, ...newImages],
                  });
                }}
                onFilesAdded={(newFiles) => {
                  const currentFiles = form.getFieldValue('attachedFiles') || [];
                  form.setFieldsValue({
                    attachedFiles: [...currentFiles, ...newFiles],
                  });
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    padding: '5px',
                    borderBottom: '1px solid #d9d9d9',
                    backgroundColor: '#fafafa',
                  }}
                >
                  <Tooltip title="Bold" mouseEnterDelay={0.5}>
                    <Button
                      icon={<BoldOutlined />}
                      onClick={() =>
                        insertText({
                          before: '**',
                          after: '**',
                        })
                      }
                      type="text"
                    />
                  </Tooltip>
                  <Tooltip title="Italic" mouseEnterDelay={0.5}>
                    <Button
                      icon={<ItalicOutlined />}
                      onClick={() =>
                        insertText({
                          before: '*',
                          after: '*',
                        })
                      }
                      type="text"
                    />
                  </Tooltip>
                  <Tooltip title="Strikethrough" mouseEnterDelay={0.5}>
                    <Button
                      icon={<StrikethroughOutlined />}
                      onClick={() =>
                        insertText({
                          before: '~~',
                          after: '~~',
                        })
                      }
                      type="text"
                    />
                  </Tooltip>
                  <Tooltip title="Ordered list" mouseEnterDelay={0.5}>
                    <Button
                      icon={<OrderedListOutlined />}
                      onClick={() =>
                        insertText({
                          transform: (selectedText, start) => {
                            if (selectedText === '') {
                              return {
                                text: '1. ',
                                newStart: start + 3,
                                newEnd: start + 3,
                              };
                            }
                            const lines = selectedText
                              .split('\n')
                              .filter((line) => line.trim() !== '');
                            const numbered = lines.map((line, i) => `${i + 1}. ${line}`).join('\n');
                            return {
                              text: numbered,
                              newStart: start,
                              newEnd: start + numbered.length,
                            };
                          },
                        })
                      }
                      type="text"
                    />
                  </Tooltip>
                  <Tooltip title="Bulleted list" mouseEnterDelay={0.5}>
                    <Button
                      icon={<UnorderedListOutlined />}
                      onClick={() =>
                        insertText({
                          transform: (selectedText, start) => {
                            if (selectedText === '') {
                              return {
                                text: '- ',
                                newStart: start + 2,
                                newEnd: start + 2,
                              };
                            }
                            const lines = selectedText
                              .split('\n')
                              .filter((line) => line.trim() !== '');
                            const dashed = lines.map((line) => `- ${line}`).join('\n');
                            return {
                              text: dashed,
                              newStart: start,
                              newEnd: start + dashed.length,
                            };
                          },
                        })
                      }
                      type="text"
                    />
                  </Tooltip>
                </div>
                <Form.Item noStyle shouldUpdate>
                  {(formInstance) => {
                    const attachedFiles = formInstance.getFieldValue('attachedFiles') || [];
                    const attachedImages = formInstance.getFieldValue('attachedImages') || [];

                    return attachedFiles.length > 0 || attachedImages.length > 0 ? (
                      <>
                        <div
                          style={{
                            backgroundColor: '#fafafa',
                            paddingInline: '8px',
                            paddingBlockStart: '5px',
                            paddingBlockEnd: '30px',
                            border: '1px solid #eee',
                            marginBottom: '8px',
                            position: 'relative',
                            zIndex: 0,
                          }}
                        >
                          {/* Files rendering */}
                          {attachedFiles.length > 0 && (
                            <Row
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '4px',
                                width: '100%',
                                padding: '4px',
                              }}
                            >
                              {attachedFiles.map((file, index) => (
                                <Tag
                                  closable
                                  key={index}
                                  onClose={() => handleRemoveFile(index)}
                                  icon={<PaperClipOutlined />}
                                  style={{
                                    margin: 0,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    maxWidth: '100%',
                                    position: 'relative',
                                    flexShrink: 0,
                                  }}
                                >
                                  {file.name.length > 30
                                    ? `${file.name.slice(0, 30)}...`
                                    : file.name}
                                </Tag>
                              ))}
                            </Row>
                          )}

                          {/* Images rendering */}
                          {attachedImages.length > 0 && (
                            <Row
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '4px',
                                width: '100%',
                                padding: '4px',
                              }}
                            >
                              {attachedImages.map((file, index) => (
                                <Tag
                                  closable
                                  key={index}
                                  onClose={() => handleRemoveImage(index)}
                                  icon={<PictureOutlined />}
                                  style={{
                                    margin: 0,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    maxWidth: '100%',
                                    position: 'relative',
                                    flexShrink: 0,
                                  }}
                                >
                                  {file.name.length > 30
                                    ? `${file.name.slice(0, 30)}...`
                                    : file.name}
                                </Tag>
                              ))}
                            </Row>
                          )}
                        </div>
                        <Row style={{ flexGrow: 1 }}>
                          <Form.Item name="comment" noStyle>
                            <Input.TextArea
                              ref={textareaRef}
                              onKeyDown={handleKeyDown}
                              onChange={(e) => {
                                form.setFieldsValue({
                                  comment: e.target.value,
                                });
                                formInstance.setFieldsValue({
                                  comment: e.target.value,
                                });
                              }}
                              autoSize={{ minRows: 8, maxRows: 16 }}
                              maxLength={3000}
                              placeholder="Enter task details"
                              style={{
                                border: 'none',
                                borderRadius: '10px',
                                outline: 'none',
                                width: '100%',
                                padding: '10px',
                                boxShadow: 'none',
                                marginTop: -30,
                              }}
                            />
                          </Form.Item>
                        </Row>
                      </>
                    ) : (
                      <Row style={{ flexGrow: 1 }}>
                        <Form.Item name="comment" noStyle>
                          <Input.TextArea
                            className="custom-textarea"
                            onKeyDown={handleKeyDown}
                            ref={textareaRef}
                            onChange={(e) => {
                              form.setFieldsValue({
                                comment: e.target.value,
                              });
                              formInstance.setFieldsValue({
                                comment: e.target.value,
                              });
                            }}
                            maxLength={3000}
                            autoSize={{ minRows: 8, maxRows: 16 }}
                            placeholder="Add a comment"
                            style={{
                              border: 'none',
                              outline: 'none',
                              width: '100%',
                              padding: '10px',
                              boxShadow: 'none',
                            }}
                          />
                        </Form.Item>
                      </Row>
                    );
                  }}
                </Form.Item>

                {/* Hidden files input */}
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: 'none' }}
                  multiple
                  accept={allowedFileExtensions.join(',')}
                  onChange={handleFileChange}
                />
                <input
                  type="file"
                  ref={imageInputRef}
                  style={{ display: 'none' }}
                  multiple
                  accept={allowedImageExtensions.join(',')}
                  onChange={handleImageChange}
                />
              </FileDropPasteArea>

              <Form.Item
                noStyle
                shouldUpdate={(prevValues, currentValues) =>
                  prevValues.isCodeSnippet !== currentValues.isCodeSnippet ||
                  prevValues.commentsCodeValue !== currentValues.commentsCodeValue
                }
              >
                {({ getFieldValue }) =>
                  getFieldValue('isCodeSnippet') &&
                  getFieldValue('commentsCodeValue') && (
                    <Col
                      style={{
                        maxWidth: '100%',
                        maxHeight: '300px',
                        overflowY: 'auto',
                        overflowX: 'auto',
                      }}
                    >
                      <CopyBlock
                        text={getFieldValue('commentsCodeValue')}
                        language="javascript"
                        wrapLongLines
                        showLineNumbers={false}
                        theme="dracula"
                        codeBlock
                      />
                    </Col>
                  )
                }
              </Form.Item>
            </Row>

            <Row
              style={{
                display: 'flex',
                paddingTop: '12px',
                width: '100%',
                justifyContent: 'space-between',
              }}
            >
              <Col style={{ display: 'flex', gap: '8px' }}>
                <Form.Item name="attachedFiles" noStyle>
                  <Tooltip title="Upload file" mouseEnterDelay={0.3}>
                    <Button
                      style={{
                        height: '35px',
                        width: '40px',
                        borderRadius: '5px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        display: 'flex',
                      }}
                      onClick={handleUploadFileClick}
                    >
                      <PaperClipOutlined
                        style={{
                          fontSize: '17px',
                        }}
                      />
                    </Button>
                  </Tooltip>
                </Form.Item>
                <Form.Item name="attachedImages" noStyle>
                  <Tooltip title="Upload image" mouseEnterDelay={0.3}>
                    <Button
                      style={{
                        height: '35px',
                        width: '40px',
                        borderRadius: '5px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        display: 'flex',
                      }}
                      onClick={handleUploadImageClick}
                    >
                      <PictureOutlined
                        style={{
                          fontSize: '17px',
                        }}
                      />
                    </Button>
                  </Tooltip>
                </Form.Item>
                <Tooltip title="Code block" mouseEnterDelay={0.3}>
                  <Button
                    style={{
                      height: '35px',
                      width: '40px',
                      borderRadius: '5px',
                      justifyContent: 'center',
                      alignItems: 'center',
                      display: 'flex',
                    }}
                    onClick={handleInsertCode}
                  >
                    <CodeOutlined
                      style={{
                        fontSize: '17px',
                      }}
                    />
                  </Button>
                </Tooltip>
              </Col>
              <Col
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  paddingBottom: '3px',
                }}
              >
                <Button
                  onClick={onButtonClick}
                  disabled={buttonDisabled}
                  style={{
                    fontSize: '15px',
                    height: '41px',
                    borderRadius: '8px',
                    backgroundColor: 'black',
                    color: 'white',
                    minWidth: '80px',
                  }}
                >
                  {buttonDisabled && <LoadingOutlined />} Submit
                </Button>
              </Col>
            </Row>
          </div>
        </Card>
      </Form>

      {deleteModal.visible && (
        <Modal
          title="Confirm Deletion"
          centered
          visible={deleteModal.visible}
          confirmLoading={buttonDisabled}
          onOk={async () => {
            try {
              setButtonDisabled(true);
              message.loading('Deleting comment...', 0);
              await updateComment({
                variables: {
                  where: { id: deleteModal.commentId },
                  data: {
                    task: { disconnect: true },
                  },
                },
              });
              await refetchTask();

              // Notify parent to refresh task list
              if (props.onTaskUpdate) {
                props.onTaskUpdate();
              }

              message.destroy();
              message.success('Comment deleted successfully');
            } catch (error) {
              message.error(`Failed to delete comment: ${error.message}`);
            } finally {
              setDeleteModal({ visible: false, commentId: null });
              setButtonDisabled(false);
            }
          }}
          onCancel={() => setDeleteModal({ visible: false, commentId: null })}
        >
          <p>Are you sure you want to delete this comment?</p>
        </Modal>
      )}
    </div>
  );
};
